import { useContext, useEffect, useState } from 'react'
import { useGetClientCreditBalanceLazyQuery, useGetPublicCreditBalanceSummaryLazyQuery } from '../../generated'
import { GRAPHQL_QUERY_POLICY } from '../constants'
import { AuthContext } from '../context/AuthContext'

interface UseGetClientCreditBalance {
  caseId?: string
  clientId?: string
  token?: string
}

interface UseGetClientCreditBalanceResult {
  creditBalance: number
  loading: boolean
  error?: any
}

const useGetClientCreditBalance = ({
  caseId,
  clientId,
  token
}: UseGetClientCreditBalance): UseGetClientCreditBalanceResult => {
  const [creditBalance, setCreditBalance] = useState<number>(0)
  const { currentUser } = useContext(AuthContext)

  const [getClientCreditBalance, { loading: isLoadingCreditBalance, error: clientCreditBalanceError }] =
    useGetClientCreditBalanceLazyQuery({
      ...GRAPHQL_QUERY_POLICY,
      variables: undefined,
      onCompleted: data => {
        if (data.getClientCreditBalance?.creditBalance != null) {
          setCreditBalance(data.getClientCreditBalance.creditBalance)
        }
      }
    })

  const [getPublicCreditBalanceSummary, { loading: isLoadingPublicCreditBalance, error: publicCreditBalanceError }] =
    useGetPublicCreditBalanceSummaryLazyQuery({
      ...GRAPHQL_QUERY_POLICY,
      variables: undefined,
      onCompleted: data => {
        const clients = data.getPublicCreditBalanceSummary?.clients
        if (clients && clientId) {
          const match = clients.find(c => c.clientId === clientId)
          if (match?.creditBalance != null) {
            setCreditBalance(match.creditBalance)
          }
        }
      }
    })

  useEffect(() => {
    if (caseId && clientId) {
      if (currentUser?.id) {
        getClientCreditBalance({
          variables: {
            getClientCreditBalanceInput: { clientId, caseId }
          }
        })
      } else {
        getPublicCreditBalanceSummary({
          variables: {
            getCreditBalanceSummaryInput: { caseId, clientId, token }
          }
        })
      }
    }
  }, [caseId, clientId, token, currentUser?.id, getClientCreditBalance, getPublicCreditBalanceSummary])

  return {
    creditBalance,
    loading: isLoadingCreditBalance || isLoadingPublicCreditBalance,
    error: clientCreditBalanceError || publicCreditBalanceError
  }
}

export default useGetClientCreditBalance
